// ── What the current user is actually entitled to ────────────────────────────
// Mirrors backend/utils/entitlements.js. Pages used to derive this inline:
//
//   const isFamily = user?.subscription?.plan === 'family' &&
//                    ['active','trialing'].includes(user?.subscription?.status);
//
// which is wrong for a co-parent. /api/auth/me returns the co-parent's OWN
// record, and a co-parent is always on the free plan — the Family subscription
// belongs to the primary parent. So the UI hid Family features from co-parents
// even where the API grants them: clone narration checks
// requireFamily(primaryUser), and share links check the primary parent too.
//
// The server now sends `user.entitlement` ({ plan, viaCoParent, primaryName })
// computed with its own effectivePlan(), so both sides agree by construction.
// The inline fallback below only matters for a client running against an older
// build of the API.

const legacyPlan = (user) => {
  const sub = user?.subscription;
  if (!sub?.plan || sub.plan === 'free') return 'free';
  // A lapsed subscription keeps `plan` and only changes `status`, so status is
  // what decides — 'trialing' is entitled, everything non-active is not.
  return ['active', 'trialing'].includes(sub.status) ? sub.plan : 'free';
};

/** The plan the user may actually use right now: 'free' | 'basic' | 'family'. */
export const entitledPlan = (user) => user?.entitlement?.plan || legacyPlan(user);

/** True when the user is entitled to any of `plans`. */
export const hasPlan = (user, plans) => plans.includes(entitledPlan(user));

/** True for Family features — the common case, including co-parents. */
export const isFamilyEntitled = (user) => entitledPlan(user) === 'family';

/** True when that entitlement comes from the primary parent, not their own sub. */
export const isViaCoParent = (user) => !!user?.entitlement?.viaCoParent || !!user?.coParentOf;

/**
 * Can this user generate stories?
 *
 * A co-parent invited as `read_only` cannot — generateStory returns 403 with
 * "Your family access level does not allow generating stories". Nothing in the
 * UI knew that, so a read-only grandparent could open the generator, pick a
 * child, choose themes and a tone, and only discover the restriction when the
 * Generate button failed at the end.
 */
export const canGenerateStories = (user) => {
  if (!user) return false;
  if (!user.coParentOf) return true;              // primary parent / normal user
  return user.coParentRole === 'can_generate';
};

export default { entitledPlan, hasPlan, isFamilyEntitled, isViaCoParent, canGenerateStories };
